import { Fragment, ReactNode, useEffect, useMemo } from 'react'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import { unescapeTags } from '@aytm/lib/common/func'
import { getMediumAssetPath } from '@aytm/lib/common/assets'
import {
  ASSETS_TYPES,
  EcommerceBannerPlacement,
  EcommerceResponse,
  IEcommerceAnswerSchema,
  IEcommerceQuestionSchema
} from '../../types.ts'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale.ts'
import { getAssetType } from '../../utils/getAssetType.ts'
import { replaceTags } from '../../utils/replaceTags.ts'
import { useChildrenProducts } from '../../hooks/useChildrenProducts.tsx'
import { readDimensions } from '../../utils/familyAxes.ts'
import Gallery from './Gallery.tsx'
import Rating from './Rating.tsx'
import VariantSelect from './VariantSelect.tsx'
import AddToCartButton from './AddToCartButton.tsx'
import ProductCard from './ProductCard.tsx'
import useCarouselNav from './useCarouselNav.ts'
import style from './Product.module.scss'
import saveToListIcon from '../assets/icons/save-to-list.svg'
import stockIcon from '../assets/icons/stock-icon.svg'
import pickupIcon from '../assets/icons/pickup.svg'
import deliveryIcon from '../assets/icons/delivery.svg'
import thirdPartyDeliveryIcon from '../assets/icons/third-party-delivery.svg'
import useAssetsCollectionBuilder from '../../hooks/useAssetsCollectionBuilder.ts'

interface IProductProps {
  answer: IEcommerceAnswerSchema
  schema: IEcommerceQuestionSchema
  response: EcommerceResponse
  findability: boolean
  cartTotal: number
  onItemClose: () => void
  onItemSelect: (index: number, bannerPlacement?: EcommerceBannerPlacement) => void
  onAddToCart: (index: number, qnt: number, inCarousel?: boolean) => void
  scrollTop: () => void
}

// Decorative fulfilment panel — no schema field feeds it, so the states are fixed.
const AVAILABILITY = [
  { icon: pickupIcon, label: 'fulfilmentPickup', status: 'available', available: true },
  { icon: deliveryIcon, label: 'fulfilmentKrogerDelivery', status: 'unavailable', available: false },
  { icon: thirdPartyDeliveryIcon, label: 'fulfilmentAllDelivery', status: 'unavailable', available: false }
]

const Product = ({
  answer,
  schema,
  response,
  findability,
  cartTotal,
  onItemClose,
  onItemSelect,
  onAddToCart,
  scrollTop
}: IProductProps) => {
  const { t, languageTag } = useSurveyLocale()
  const viewport = useViewport()

  const assetsCollection = useAssetsCollectionBuilder(answer)

  const family = useChildrenProducts({ answer, schema })
  // "You May Also Like" is a measured promotional placement: featured top-level products only,
  // never the whole catalogue. Excludes the current family, which the variant dropdowns cover.
  const suggestions = useMemo(() => {
    const familyIds = new Set(family.map(product => product.id))
    return schema.answers.filter(
      product => product.featured && !product.na && !product.parent_product && !familyIds.has(product.id)
    )
  }, [schema.answers, family])
  const { trackRef, nav, measureNav, slide } = useCarouselNav(suggestions.length)

  useEffect(() => {
    scrollTop()
  }, [answer.id, scrollTop])

  const amount = response.answers[answer.index].v
  const hasDiscount = Boolean(answer.discounted_price) && answer.price > answer.discounted_price
  const price = (value: number) => formatPriceWithLocale(value, schema.price_mask, languageTag, true, false)
  // No schema field carries a barcode: `market_place_id` is an arbitrary marketplace key (it is
  // what `parent_product` matches on), so labelling it "UPC" — padded or verbatim — asserts a
  // product fact the researcher never supplied. The identity line shows dimensions only.
  const dimensionValues = readDimensions(answer)
    .map(dimension => dimension.value)
    .filter(Boolean)
    .join(', ')

  const onVariantSelect = (index: number) => {
    onItemClose()
    onItemSelect(index)
  }

  const detailsHtml = replaceTags(unescapeTags(answer.description || ''))
  // kroger.com's Product Details panel repeats the lead product photo beside the copy;
  // the gallery can carry videos, so the fallback must pick an actual image
  const detailsImage = answer.image || answer.gallery?.find(asset => ASSETS_TYPES.IMAGE === getAssetType(asset))
  // kroger.com shows dietary facts as one comma-separated line — CSS joins the stored list (see .dietary)
  const dietaryHtml = replaceTags(unescapeTags(answer.feature_bullets || ''))
  const nutritionRows = answer.nutrition_information || []
  const infoSections = (
    [
      ['ingredients', answer.ingredient_statement],
      ['allergenInfo', answer.allergens_description]
    ] as const
  ).filter(([, text]) => text)
  const nutritionBody =
    nutritionRows.length > 0 || infoSections.length > 0 ? (
      <div className={`${style.details} ${style.nutritionPanel}`}>
        {nutritionRows.length > 0 && (
          <div className={style.factsBox}>
            <div className={style.factsTitle}>{t('questions', 'ec', 'kroger', 'nutritionFactsTitle')}</div>
            <div className={style.nutritionFacts}>
              {nutritionRows.map(row => (
                <div key={row.name} className={`${style.nutritionRow} ${row.hero ? style.nutritionCalories : ''}`}>
                  <span className={style.nutritionName}>{row.name}</span>
                  <span>{row.value}</span>
                </div>
              ))}
            </div>
            <div className={style.dvReference}>{t('questions', 'ec', 'kroger', 'dvReference')}</div>
          </div>
        )}
        <div className={style.nutritionText}>
          {infoSections.map(([key, text]) => (
            <Fragment key={key}>
              <h4 className={style.infoSubheading}>{t('questions', 'ec', 'kroger', key)}</h4>
              <div dangerouslySetInnerHTML={{ __html: replaceTags(unescapeTags(text as string)) }} />
            </Fragment>
          ))}
          <h4 className={style.infoSubheading}>{t('questions', 'ec', 'kroger', 'disclaimer')}</h4>
          <div>
            {t('questions', 'ec', 'kroger', 'disclaimerText')}{' '}
            <EmptyLink color="#1860ae">
              <span className={`${style.link} ${style.readMore}`}>{t('questions', 'ec', 'kroger', 'readMore')}</span>
            </EmptyLink>
          </div>
        </div>
      </div>
    ) : null
  const titleBlock = <h1 className={style.title} dangerouslySetInnerHTML={{ __html: answer.text }} />

  const ratingBlock = (
    <div className={style.ratingRow}>
      <Rating rating={answer.rating || 0} />
      <span className={style.ratingValue}>{(answer.rating || 0).toFixed(1)}</span>
      <span className={style.ratingCount}>({answer.reviews || 0})</span>
      {!viewport.mobile && (
        <EmptyLink color="#1860ae">
          <span className={style.link}>{t('questions', 'ec', 'kroger', 'viewAllReviews')}</span>
        </EmptyLink>
      )}
    </div>
  )

  const aisleBlock = (
    <div className={style.aisle}>
      <i className="pi pi-map-marker" />
      <span>{t('questions', 'ec', 'kroger', 'locatedNearYou')}</span>
    </div>
  )

  const redLabelBlock = answer.red_label ? (
    <div className={style.redLabel} dangerouslySetInnerHTML={{ __html: answer.red_label }} />
  ) : null

  const lowStockBlock = answer.overal_pick ? (
    <div className={style.lowStock}>
      <img src={stockIcon} alt="" role="presentation" className={style.lowStockIcon} />
      {t('questions', 'ec', 'kroger', 'lowStock')}
    </div>
  ) : null

  const priceRow = (
    <div className={style.priceRow}>
      {hasDiscount ? (
        <>
          <span className={style.priceSale} dangerouslySetInnerHTML={{ __html: price(answer.discounted_price) }} />
          <span className={style.priceOld} dangerouslySetInnerHTML={{ __html: price(answer.price) }} />
        </>
      ) : (
        <span className={style.price} dangerouslySetInnerHTML={{ __html: price(answer.price) }} />
      )}
    </div>
  )

  const perCountBlock = answer.per_count_price ? (
    <div className={style.perCount} dangerouslySetInnerHTML={{ __html: unescapeTags(answer.per_count_price) }} />
  ) : null

  const snapBlock = answer.amazon_choice ? (
    <div className={style.snapEbt}>{t('questions', 'ec', 'kroger', 'snapEbtEligible')}</div>
  ) : null

  // With a body — a real open disclosure; without — the decorative hint-only header.
  const accordion = (title: string, body?: ReactNode) =>
    body ? (
      <details className={style.panel} open={true}>
        <summary className={style.panelHeader}>
          <span className={style.panelTitle}>{title}</span>
          <i className={`pi pi-chevron-down ${style.chevron}`} />
        </summary>
        <div className={style.panelBody}>{body}</div>
      </details>
    ) : (
      <div className={`${style.panel} ${style.panelInert}`}>
        <EmptyLink color="#1860ae">
          <div className={style.panelHeader}>
            <span className={style.panelTitle}>{title}</span>
            <i className={`pi pi-chevron-down ${style.chevron}`} />
          </div>
        </EmptyLink>
      </div>
    )

  return (
    <section
      className={`${style.product} ${viewport.mobile ? style.mobile : ''} ${
        cartTotal > 0 && !findability && !viewport.mobile ? style.openRail : ''
      }`}
      data-testid="SW-Kroger-Product"
    >
      <div className={style.inner}>
        <nav className={style.breadcrumbs}>
          <span className={style.crumb}>
            <span className={style.crumbLink} onClick={onItemClose}>
              {t('questions', 'ec', 'kroger', 'home')}
            </span>
            <i className="pi pi-chevron-right" />
          </span>
          <span className={style.crumb}>
            <span className={style.crumbLink} onClick={onItemClose}>
              {schema.search_condition}
            </span>
            <i className="pi pi-chevron-right" />
          </span>

          <span className={style.crumbCurrent} dangerouslySetInnerHTML={{ __html: answer.text }} />
        </nav>

        <div className={style.columns}>
          {assetsCollection.length > 0 ? (
            <Gallery key={answer.id} answer={answer} assetsCollection={assetsCollection} />
          ) : (
            <div className={style.galleryEmpty} />
          )}

          <div className={style.info}>
            {viewport.mobile ? (
              <>
                {aisleBlock}
                {lowStockBlock}
                {redLabelBlock}
                <div className={style.priceRatingRow}>
                  {priceRow}
                  {ratingBlock}
                </div>
                {perCountBlock}
                {snapBlock}
                {titleBlock}
              </>
            ) : (
              <>
                {titleBlock}
                {ratingBlock}
                {dimensionValues && (
                  <div className={style.identity}>
                    <span>{dimensionValues}</span>
                  </div>
                )}
                {aisleBlock}
                {lowStockBlock}
                <div className={style.priceBlock}>
                  {redLabelBlock}
                  {priceRow}
                  {perCountBlock}
                  {snapBlock}
                </div>
              </>
            )}

            <VariantSelect schema={schema} answer={answer} onSelect={onVariantSelect} />

            <div className={style.availability}>
              <div className={style.availabilityTitle}>{t('questions', 'ec', 'kroger', 'itemAvailability')}</div>
              <div className={style.availabilityRow}>
                {AVAILABILITY.map(mode => (
                  <div className={style.availabilityMode} key={mode.label}>
                    <img src={mode.icon} alt="" role="presentation" className={style.availabilityIcon} />
                    <div className={style.availabilityText}>
                      <span className={style.availabilityLabel}>{t('questions', 'ec', 'kroger', mode.label)}</span>
                      <span className={`${style.availabilityStatus} ${mode.available ? style.available : ''}`}>
                        {t('questions', 'ec', 'kroger', mode.status)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {!viewport.mobile && (
              <div className={style.addToCart} data-testid="SW-EC-PDP-AddToCartSidebar">
                <AddToCartButton
                  amount={amount}
                  findability={findability}
                  limit={schema.max_qty_per_product}
                  onAdd={qnt => onAddToCart(answer.index, qnt)}
                />
              </div>
            )}

            {/* Row wrapper, not bare EmptyLink: the wrapper span is what `.info` lays out, so
                centering and the mobile hide have to apply to an element we own. */}
            <div className={style.saveToListRow}>
              <EmptyLink color="#1860ae">
                <div className={style.saveToList}>
                  <img src={saveToListIcon} alt="" role="presentation" className={style.saveToListIcon} />
                  {t('questions', 'ec', 'kroger', 'saveToList')}
                </div>
              </EmptyLink>
            </div>

            {hasDiscount && (
              <div className={style.savings}>
                <div className={style.savingsTitle}>{t('questions', 'ec', 'kroger', 'savings')}</div>
                <span className={style.coupon}>{t('questions', 'ec', 'kroger', 'coupon')}</span>
                <div className={style.savingsRow}>
                  <EmptyLink color="#1860ae">
                    <span className={style.savingsCheck} />
                  </EmptyLink>
                  <span
                    className={style.savingsText}
                    dangerouslySetInnerHTML={{
                      __html: t<(amount: string) => string>(
                        'questions',
                        'ec',
                        'kroger',
                        'saveAmount'
                      )(price(answer.price - answer.discounted_price))
                    }}
                  />
                  <EmptyLink color="#1860ae">
                    <span className={style.link}>{t('questions', 'ec', 'kroger', 'shopDeal')}</span>
                  </EmptyLink>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={style.productInfo}>
          <h2 className={style.sectionTitle}>{t('questions', 'ec', 'kroger', 'productInformation')}</h2>
          <div className={style.accordion}>
            {accordion(
              t('questions', 'ec', 'kroger', 'productDetails'),
              detailsHtml.trim() ? (
                <div className={style.detailsBody}>
                  {detailsImage && (
                    <img
                      className={style.detailsImage}
                      src={getMediumAssetPath(detailsImage)}
                      alt={detailsImage.alt_text || ''}
                      {...(detailsImage.alt_text ? {} : { role: 'presentation' })}
                    />
                  )}
                  <div className={style.details} dangerouslySetInnerHTML={{ __html: detailsHtml }} />
                </div>
              ) : (
                <div className={style.details}>{t('questions', 'ec', 'kroger', 'noProductDetails')}</div>
              )
            )}
            {accordion(
              t('questions', 'ec', 'kroger', 'dietaryInformation'),
              dietaryHtml.trim() ? (
                <div
                  className={`${style.details} ${style.dietary}`}
                  dangerouslySetInnerHTML={{ __html: dietaryHtml }}
                />
              ) : null
            )}
            {accordion(t('questions', 'ec', 'kroger', 'nutritionInformation'), nutritionBody)}
          </div>
        </div>

        {suggestions.length > 0 && (
          <div className={style.suggestions}>
            <h2 className={style.sectionTitle}>{t('questions', 'ec', 'kroger', 'youMayAlsoLike')}</h2>
            <div className={style.suggestionsViewport}>
              {nav.overflowing && !viewport.mobile && (
                <>
                  <button
                    type="button"
                    className={`${style.navBtn} ${style.navBtnLeft}`}
                    disabled={nav.atStart}
                    onClick={() => slide(-1)}
                    aria-label={t('questions', 'ec', 'kroger', 'previous')}
                  >
                    <i className="pi pi-chevron-left" />
                  </button>
                  <button
                    type="button"
                    className={`${style.navBtn} ${style.navBtnRight}`}
                    disabled={nav.atEnd}
                    onClick={() => slide(1)}
                    aria-label={t('questions', 'ec', 'kroger', 'next')}
                  >
                    <i className="pi pi-chevron-right" />
                  </button>
                </>
              )}
              <div ref={trackRef} className={style.suggestionsRow} onScroll={measureNav}>
                {suggestions.map(product => (
                  <div className={style.suggestionCard} key={product.id}>
                    <ProductCard
                      data-testid="SW-EC-CarouselItem"
                      schema={schema}
                      answer={product}
                      amount={response.answers[product.index].v}
                      findability={findability}
                      compact
                      disabled={'focus' == schema.mode_type && !product.focused}
                      bannerPlacement="featured"
                      onSelect={() => {
                        onItemClose()
                        onItemSelect(product.index, 'featured')
                      }}
                      onAdd={qnt => onAddToCart(product.index, qnt, true)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {viewport.mobile && (
        <div className={style.stickyBar} data-testid="SW-EC-PDP-AddToCartSidebar">
          <EmptyLink color="#1860ae">
            <span className={style.stickySave}>
              <img src={saveToListIcon} alt="" role="presentation" className={style.saveToListIcon} />
            </span>
          </EmptyLink>
          <div className={style.stickyCta}>
            <AddToCartButton
              amount={amount}
              findability={findability}
              limit={schema.max_qty_per_product}
              spread
              onAdd={qnt => onAddToCart(answer.index, qnt)}
            />
          </div>
        </div>
      )}
    </section>
  )
}

export default Product
